import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { problemMessage } from '../api/problem';
import { StateMessage } from '../ui/StateMessage';
import { TenantItem } from './TenantItem';
import type { TenantRow, useTenants } from './use-tenants';

type ListState = ReturnType<typeof useTenants>['state'];

function ListFoot({
  state,
  more,
  reload,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div className="people-list-foot">
      <span>
        {state.status === 'ready' ? t('tenants.total', { count: state.page.total }) : null}
        <span className="text-muted-foreground"> · {t('tenants.peopleNote')}</span>
      </span>
      {state.status === 'ready' && state.hasMore ? (
        <Button variant="outline" size="sm" onClick={more} disabled={state.loadingMore}>
          {state.loadingMore ? t('common.loading') : t('common.more')}
        </Button>
      ) : null}
      {state.status === 'error' ? (
        <span role="alert" className="flex items-center gap-2">
          {problemMessage(t, state.code, state.httpStatus)}
          <Button variant="outline" size="sm" onClick={reload}>
            {t('common.retry')}
          </Button>
        </span>
      ) : null}
    </div>
  );
}

function ListSkeleton() {
  const { t } = useTranslation('administration');
  return (
    <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-2">
      {[0, 1, 2].map((n) => (
        <Skeleton key={n} className="h-16 w-full" />
      ))}
    </SkeletonRegion>
  );
}

function LoadError({
  state,
  reload,
}: {
  readonly state: Extract<ListState, { status: 'error' }>;
  readonly reload: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <StateMessage
      role="alert"
      tone="error"
      icon={Building2}
      title={t('tenants.loadError')}
      text={problemMessage(t, state.code, state.httpStatus)}
      action={
        <Button variant="outline" onClick={reload}>
          {t('common.retry')}
        </Button>
      }
    />
  );
}

function ListRows({
  items,
  cardHrefFor,
  onRename,
}: {
  readonly items: readonly TenantRow[];
  readonly cardHrefFor: (row: TenantRow) => string;
  readonly onRename: ((row: TenantRow) => void) | null;
}) {
  const { t } = useTranslation('administration');
  return (
    <div
      className="people-list place-list tenants-list"
      role="list"
      aria-label={t('tenants.listLabel')}
    >
      <div className="people-list-head" aria-hidden="true">
        <span>{t('tenants.columns.tenant')}</span>
        <span>{t('tenants.columns.organizations')}</span>
        <span>{t('tenants.columns.people')}</span>
        <span>{t('tenants.columns.created')}</span>
        <span />
      </div>
      {items.map((row) => (
        <TenantItem
          key={row.id}
          row={row}
          cardHref={cardHrefFor(row)}
          onRename={onRename === null ? null : () => onRename(row)}
        />
      ))}
    </div>
  );
}

/** The list of tenants, or the loading, error or empty state that stands in for it. */
export function TenantListPane({
  state,
  more,
  reload,
  q,
  setQuery,
  canCreate,
  setRenaming,
  cardHrefFor,
}: {
  readonly state: ListState;
  readonly more: () => void;
  readonly reload: () => void;
  readonly q: string;
  readonly setQuery: (value: string) => void;
  readonly canCreate: boolean;
  readonly setRenaming: (row: TenantRow) => void;
  readonly cardHrefFor: (row: TenantRow) => string;
}) {
  const { t } = useTranslation('administration');
  if (state.status === 'loading' && state.items.length === 0) return <ListSkeleton />;
  if (state.status === 'error' && state.items.length === 0) {
    return <LoadError state={state} reload={reload} />;
  }
  if (state.items.length === 0) {
    return (
      <StateMessage
        icon={Building2}
        title={q === '' ? t('tenants.firstUse') : t('tenants.noResults')}
        {...(q === ''
          ? {}
          : {
              action: (
                <Button variant="outline" onClick={() => setQuery('')}>
                  {t('organizations.clear')}
                </Button>
              ),
            })}
      />
    );
  }
  return (
    <>
      <ListRows
        items={state.items}
        cardHrefFor={cardHrefFor}
        onRename={canCreate ? setRenaming : null}
      />
      <ListFoot state={state} more={more} reload={reload} />
    </>
  );
}
